import React, { useState } from 'react';
import { version } from '../version';
import { Briefcase, Users, ClipboardList, LayoutDashboard, Settings, Scale, UserCheck, Network, ChevronDown, Upload, Plus, Pin, PinOff, FileCheck, Zap, Share2, Laptop, Globe, Building2, UserCog, Database, Cog, Star } from 'lucide-react';
import clsx from 'clsx';
import { Position, User } from '../types';


export type NavModule = 'home' | 'needs' | 'positions' | 'candidates' | 'offer_approval' | 'onboarding' | 'it_resources' | 'referral' | 'talent_hub' | 'headcount' | 'boss_brief' | 'dashboard' | 'team_management' | 'system_settings' | 'website_applicants' | 'ehr_main' | 'ehr_data' | 'ehr_system';

interface Props {
  activeModule: NavModule;
  onModuleChange: (m: NavModule) => void;
  currentUser: User;
  onLogout: () => void;
  onOpenTeamManager: () => void;
  positions?: Position[];
  selectedPositionId?: string;
  onPositionChange?: (id: string) => void;
  totalCandidateCount?: number;
  onAddCandidate?: () => void;
  onBatchImport?: (positionId: string) => void;
  pinnedPositionIds?: string[];
  onTogglePinPosition?: (positionId: string) => void;
  onOpenWebsiteApplicants?: () => void;
  favoritesActive?: boolean;
  onOpenFavorites?: () => void;
  hideBrand?: boolean;
  variant?: 'default' | 'card';
  hideUserFooter?: boolean;
}

const NAV_ITEMS: { key: NavModule; label: string; icon: React.ElementType; hrOnly?: boolean; itOnly?: boolean; adminOnly?: boolean; referrerOnly?: boolean; groupLabel?: string }[] = [
  { key: 'home', label: '首页待办', icon: ClipboardList },
  { key: 'headcount', label: '编制管理', icon: Building2, hrOnly: true },
  { key: 'positions', label: '岗位管理', icon: Briefcase, hrOnly: true },
  { key: 'candidates', label: '候选人管理', icon: Users },
  { key: 'offer_approval', label: 'Offer审批', icon: FileCheck, hrOnly: true },
  { key: 'onboarding', label: '待入职管理', icon: UserCheck, hrOnly: true },
  { key: 'it_resources', label: 'IT资源工单', icon: Laptop, itOnly: true },
  { key: 'referral', label: '我的内推', icon: Share2 },
  { key: 'boss_brief', label: 'Boss简报', icon: Zap, hrOnly: true },
  { key: 'talent_hub', label: '人才中枢', icon: Network, hrOnly: true },
  { key: 'dashboard', label: '仪表盘', icon: LayoutDashboard },
  // ── eHR 花名册（数据中心） ──
  { key: 'ehr_main', label: '花名册', icon: UserCog, hrOnly: true, groupLabel: 'eHR 花名册' },
  { key: 'ehr_data', label: '数据管理', icon: Database, hrOnly: true },
  { key: 'ehr_system', label: '系统管理', icon: Cog, hrOnly: true },
  // ── 系统 ──
  { key: 'team_management', label: '团队管理', icon: Settings, adminOnly: true },
  { key: 'system_settings', label: '系统设置', icon: Settings, adminOnly: true },
];

export default function Sidebar({ activeModule, onModuleChange, currentUser, onOpenTeamManager, positions, selectedPositionId, onPositionChange, totalCandidateCount, onAddCandidate, onBatchImport, pinnedPositionIds = [], onTogglePinPosition, onOpenWebsiteApplicants, favoritesActive = false, onOpenFavorites, hideBrand = false, variant = 'default', hideUserFooter = false }: Props) {
  const isHr = currentUser.role === 'admin' || currentUser.role === 'hr' || currentUser.role === 'vip';
  const isAdmin = currentUser.role === 'admin';
  const isReferrer = currentUser.role === 'referrer';
  const isIt = currentUser.role === 'it';
  const [positionListOpen, setPositionListOpen] = useState(true);
  const visiblePositions = (positions || []).filter(p => p.status === 'open');

  return (
    <aside className={clsx(
      'flex flex-col h-full bg-white',
      variant === 'card'
        ? 'w-full rounded-2xl border border-gray-200 overflow-hidden'
        : 'w-56 min-w-[220px] border-r border-gray-200'
    )}>
      {/* Brand */}
      {!hideBrand && (
        <div className="px-4 py-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center shadow-sm">
              <Briefcase size={17} className="text-white" />
            </div>
            <span className="font-bold text-gray-900 text-[15px] tracking-tight">留形面试官</span>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">
        {NAV_ITEMS.filter(item => {
          // team_management & system_settings are always admin-only
          if (item.key === 'team_management' || item.key === 'system_settings') return isAdmin;
          // eHR 花名册三块：HR/admin/vip 可见，绕过 module 权限表（新模块不在旧权限里）
          if (item.key === 'ehr_main' || item.key === 'ehr_data' || item.key === 'ehr_system') return isHr;
          // Use effective module permissions when available
          const allowedModules = currentUser.permissions?.modules;
          if (allowedModules) return allowedModules.includes(item.key);
          // Fallback to legacy role-based gating
          if (isReferrer) return item.key === 'referral';
          if (isIt) return item.key === 'it_resources';
          if (item.itOnly) return isAdmin;
          if (item.adminOnly) return isAdmin;
          return !item.hrOnly || isHr;
        }).map(item => {
          const Icon = item.icon;
          const label = item.key === 'referral' && isHr ? '内推信息管理' : item.label;
          const isCandidates = item.key === 'candidates';
          const active = activeModule === item.key || (isCandidates && activeModule === 'website_applicants');

          return (
            <React.Fragment key={item.key}>
              {item.groupLabel && (
                <div className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-300">
                  {item.groupLabel}
                </div>
              )}
              <button
                onClick={() => {
                  if (isCandidates && active) {
                    setPositionListOpen(!positionListOpen);
                  } else {
                    onModuleChange(item.key);
                    if (isCandidates) setPositionListOpen(true);
                  }
                }}
                className={clsx(
                  'w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-colors',
                  active ? 'bg-primary-50 text-primary-700' : 'text-gray-600 hover:bg-gray-50'
                )}
              >
                <Icon size={17} className={active ? 'text-primary-500' : 'text-gray-400'} />
                <span className="text-sm font-medium flex-1">{label}</span>
                {isCandidates && visiblePositions.length > 0 && (
                  <ChevronDown size={14} className={clsx('text-gray-400 transition-transform', positionListOpen && activeModule === 'candidates' ? 'rotate-0' : '-rotate-90')} />
                )}
              </button>

              {/* Collapsible position sub-list under 候选人管理 */}
              {isCandidates && (activeModule === 'candidates' || activeModule === 'website_applicants') && positionListOpen && positions && onPositionChange && (
                <div className="ml-4 pl-2 border-l border-gray-100 space-y-0.5 py-1">
                  {isHr && onAddCandidate && (
                    <button
                      onClick={onAddCandidate}
                      className="w-full flex items-center justify-center gap-1.5 px-2 py-2 mb-1 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors shadow-sm"
                    >
                      <Plus size={13} />添加候选人
                    </button>
                  )}
                  <button
                    onClick={() => onPositionChange('')}
                    className={clsx(
                      'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors',
                      !selectedPositionId && !favoritesActive && activeModule === 'candidates' ? 'bg-primary-50 text-primary-700' : 'text-gray-500 hover:bg-gray-50'
                    )}
                  >
                    <Users size={13} className={!selectedPositionId && !favoritesActive && activeModule === 'candidates' ? 'text-primary-500' : 'text-gray-400'} />
                    <span className="text-xs font-medium flex-1">人才池</span>
                  </button>

                  {/* 我的收藏入口（跨池个人书签视图，不影响归属/占用） */}
                  {onOpenFavorites && (
                    <button
                      onClick={onOpenFavorites}
                      className={clsx(
                        'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors',
                        favoritesActive && activeModule === 'candidates' ? 'bg-amber-50 text-amber-700' : 'text-gray-500 hover:bg-gray-50'
                      )}
                    >
                      <Star size={13} fill={favoritesActive && activeModule === 'candidates' ? 'currentColor' : 'none'} className={favoritesActive && activeModule === 'candidates' ? 'text-amber-500' : 'text-gray-400'} />
                      <span className="text-xs font-medium flex-1">我的收藏</span>
                    </button>
                  )}

                  {/* 官网投递入口 */}
                  {isHr && onOpenWebsiteApplicants && (
                    <button
                      onClick={onOpenWebsiteApplicants}
                      className={clsx(
                        'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors',
                        String(activeModule) === 'website_applicants' ? 'bg-primary-50 text-primary-700' : 'text-gray-500 hover:bg-gray-50'
                      )}
                    >
                      <Globe size={13} className={String(activeModule) === 'website_applicants' ? 'text-primary-500' : 'text-gray-400'} />
                      <span className="text-xs font-medium flex-1">官网投递</span>
                    </button>
                  )}

                  {/* Pinned positions first */}
                  {[...visiblePositions].sort((a, b) => {
                    const aPinned = pinnedPositionIds.includes(a.id) ? 0 : 1;
                    const bPinned = pinnedPositionIds.includes(b.id) ? 0 : 1;
                    return aPinned - bPinned;
                  }).map(p => {
                    const isPinned = pinnedPositionIds.includes(p.id);
                    return (
                    <button
                      key={p.id}
                      onClick={() => onPositionChange(p.id)}
                      className={clsx(
                        'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors group',
                        selectedPositionId === p.id ? 'bg-primary-50 text-primary-700' : 'text-gray-500 hover:bg-gray-50'
                      )}
                    >
                      <Briefcase size={12} className={selectedPositionId === p.id ? 'text-primary-500' : 'text-gray-400'} />
                      <span className="text-xs font-medium flex-1 truncate">{p.name}</span>
                      <div className="flex items-center gap-0.5 shrink-0">
                        {onTogglePinPosition && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onTogglePinPosition(p.id); }}
                            className={clsx('p-0.5 rounded transition-all', isPinned ? 'text-primary-500' : 'text-gray-300 hover:text-primary-500 opacity-0 group-hover:opacity-100')}
                            title={isPinned ? '取消置顶' : '置顶'}
                          >
                            {isPinned ? <Pin size={10} /> : <PinOff size={10} />}
                          </button>
                        )}
                        {isHr && onBatchImport && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onBatchImport(p.id); }}
                            className="p-0.5 rounded text-gray-300 hover:text-primary-500 opacity-0 group-hover:opacity-100 transition-all"
                            title="批量导入"
                          >
                            <Upload size={10} />
                          </button>
                        )}
                        {p.status === 'paused' && <span className="w-1.5 h-1.5 rounded-full bg-gray-300" title="已暂停" />}
                        <span className="text-[10px] text-gray-400">{p.candidate_count || 0}</span>
                      </div>
                    </button>
                    );
                  })}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </nav>

      {/* User info footer */}
      {!hideUserFooter && (
      <div className="border-t border-gray-100 px-3 py-3 space-y-2">
        <div className="flex items-center px-3 py-1.5">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center text-xs font-bold text-primary-600 shrink-0">
              {currentUser.display_name?.charAt(0) || currentUser.username.charAt(0)}
            </div>
            <div className="min-w-0">
              <div className="text-xs font-medium text-gray-700 truncate">{currentUser.display_name || currentUser.username}</div>
              <div className="text-[10px] text-gray-400">
                {currentUser.role === 'admin' ? '管理员' : currentUser.role === 'hr' ? 'HR' : currentUser.role === 'vip' ? 'VIP面试官' : currentUser.role === 'referrer' ? '内推官' : currentUser.role === 'it' ? 'IT资源官' : '面试官'}
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
      <div className="px-3 pb-2 text-center">
        <span className="text-[10px] text-gray-300 select-none">v{version}</span>
      </div>
    </aside>
  );
}
